import { DivConst } from 'constants/divConst';

import type { useApiState } from 'hooks/useApiState';
import type { AcctStateType } from 'stores/atoms/acctState';
import type { DiagnosisResponseType } from 'types/api/diagnosisResponseType';
import type { DiagnosisEditFormStateType } from 'types/page/diagnosisEditFormStateType';
import { convResultListIdToTitle, convResultListTitleToId } from 'utils/diagnosis';
import { getProductionUrl } from 'utils/terms';

// ------------------------------------------------------------------ //
// 型定義
// ------------------------------------------------------------------ //

type UseApiStateReturn = ReturnType<typeof useApiState>;

/**
 * postDiagnosisRegister の引数型から companyId / diagnosisStatusDiv を除いた共通パラメータ型。
 * postDiagnosisUpdate へのスプレッドにも利用する。
 * popupImageKey は hook 側で別途セットするため含まない。
 * reportMailDescribeHtml は postDiagnosisRegister の型定義に含まれないが既存実装で送信しているため
 * intersection で補完する。
 */
export type DiagnosisCommonParam = Omit<
  Parameters<UseApiStateReturn['postDiagnosisRegister']>[0],
  'companyId' | 'diagnosisStatusDiv'
> & {
  reportMailDescribeHtml?: string | null;
};

/**
 * postDiagnosisAiQuestions の request（currentContent）型。
 */
export type DiagnosisAiContentInput = Parameters<
  UseApiStateReturn['postDiagnosisAiQuestions']
>[0]['currentContent'];

/**
 * postDiagnosisAiQuestions のレスポンス（answerContent）から取り出した問題アイテム型。
 */
export type AiDiagnosisQuestion = NonNullable<
  Awaited<ReturnType<UseApiStateReturn['postDiagnosisAiQuestions']>>['answerContent']
>['questions'][number];

// ------------------------------------------------------------------ //
// convertFormValuesToParam
// ------------------------------------------------------------------ //

/**
 * select / radio 由来の文字列値を number | null に型強制する。
 * HTML フォームは select の value を常に文字列で返すため、API スキーマ（number | null）に揃える。
 * - null / undefined / 空文字 → null
 * - それ以外 → Number() で数値化
 */
function toNumberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  return Number(value);
}

/**
 * react-hook-form のフォーム値を API 送信パラメータに変換する。
 * - popupImageKey は含まない（hook 側で別途セットする）
 * - questionImageKey はファルシー値を null に変換する
 * - result.imageKey はファルシー値を null に変換する
 * - lineFlag は DivConst の数値から boolean に変換する
 * - options の diagnosisResultList は convResultListIdToTitle で id→title に変換する
 * - respondentMailDiv / respondentFields の div 値は toNumberOrNull で number | null に統一する
 *   （HTML select は値を文字列で返すため、API スキーマ型に合わせて変換する）
 * - reportMailSignatureHtml / reportMailDescribeHtml / description / results[].descriptionHtml は
 *   undefined の場合に null を明示的に送る
 *   （undefined のままだと JSON.stringify でキーが脱落し API required 検証で 400 になる）
 */
export function convertFormValuesToParam(values: DiagnosisEditFormStateType): DiagnosisCommonParam {
  const resultsMapped = values.results.map(result => ({
    id: result.id,
    title: result.title,
    descriptionHtml: result.descriptionHtml,
    imageKey: result.imageKey,
  }));

  return {
    title: values.title,
    titleHiddenFlag: values.titleHiddenFlag,
    description: values.description ?? null,
    respondentMailDiv: toNumberOrNull(values.respondentMailDiv),
    imageKey: values.imageKey,
    reportMailSubject: values.reportMailSubject,
    reportMailSenderName: values.reportMailSenderName,
    reportMailSignatureHtml: values.reportMailSignatureHtml ?? null,
    reportMailDescribeHtml: values.reportMailDescribeHtml ?? null,
    diagnosisTemplateId: values.diagnosisTemplateId,
    publishStartAt: values.publishStartAt,
    publishEndAt: values.publishEndAt,
    noIndexFlag: values.noIndexFlag,
    benchmarkContactListId: values.benchmarkContactListId,
    gtmId: values.gtmId,
    respondentFields: values.respondentFields.map(field => ({
      getFlag: field.getFlag,
      respondentFieldDiv: toNumberOrNull(field.respondentFieldDiv),
      respondentStandardFieldDiv: toNumberOrNull(field.respondentStandardFieldDiv),
      label: field.label,
      placeholder: field.placeholder,
      respondentCustomFieldDiv: toNumberOrNull(field.respondentCustomFieldDiv),
      respondentCustomFieldInputDiv: toNumberOrNull(field.respondentCustomFieldInputDiv),
      maxLength: toNumberOrNull(field.maxLength),
    })),
    lineFlag: Number(values.lineFlag) === DivConst.items.lineFlag.enable.value,
    lineUrl: values.lineUrl,
    questions: values.questions.map(question => ({
      title: question.title,
      questionImageKey: question.questionImageKey || null,
      options: question.options.map(option => ({
        title: option.title,
        diagnosisResultList: convResultListIdToTitle(
          option.diagnosisResultList,
          resultsMapped as Parameters<typeof convResultListIdToTitle>[1]
        ),
      })),
    })),
    results: values.results.map(result => ({
      title: result.title,
      descriptionHtml: result.descriptionHtml ?? null,
      imageKey: result.imageKey || null,
    })),
  };
}

// ------------------------------------------------------------------ //
// buildInitialDiagnosisForm
// ------------------------------------------------------------------ //

/**
 * 新規診断作成時のフォーム初期値を生成する。
 * reportMailSenderAddress は hook 側で config.ses.from.address から設定するため含まない。
 */
export function buildInitialDiagnosisForm(
  acct: AcctStateType
): Partial<DiagnosisEditFormStateType> {
  const company = acct.company;

  const termsUrl =
    company.termsInputDiv === DivConst.items.termsInputType.url.value
      ? (company.termsUrl ?? '')
      : getProductionUrl(company.cd);

  return {
    privacyPolicyUrl: company.privacyPolicyUrl ?? '',
    termsUrl,
    copyright: company.copyright ?? '',
    reportMailReplyAddress: company.reportMailReplyAddress ?? '',
    reportMailSenderName: company.reportMailSenderName ?? '',
    reportMailSignatureHtml: company.reportMailSignatureHtml ?? '',
    publishStartAt: null,
    publishEndAt: null,
    noIndexFlag: false,
    diagnosisTemplateId: 1,
    respondentFieldGroupId: null,
    respondentFields: [],
    popupImageKey: null,
    questions: [
      {
        options: [{}, {}, {}, {}],
      },
    ],
    results: [
      { id: Math.random(), isNew: true },
      { id: Math.random(), isNew: true },
      { id: Math.random(), isNew: true },
    ],
  };
}

// ------------------------------------------------------------------ //
// buildEditFormFromResponse
// ------------------------------------------------------------------ //

/**
 * API レスポンスをフォーム state に変換する（編集画面初期化用）。
 * - lineFlag は boolean → DivConst の数値に変換する
 * - respondentFields は id / no を含めてそのままマッピングする（既存実装温存）
 * - questions / results の必要なフィールドを抽出する
 */
export function buildEditFormFromResponse(
  response: DiagnosisResponseType
): Partial<DiagnosisEditFormStateType> {
  return {
    title: response.title,
    titleHiddenFlag: response.titleHiddenFlag,
    description: response.description,
    respondentMailDiv: response.respondentMailDiv,
    imageKey: response.imageKey,
    popupImageKey: response.popupImageKey,
    reportMailSubject: response.reportMailSubject,
    reportMailSenderName: response.reportMailSenderName,
    reportMailSignatureHtml: response.reportMailSignatureHtml,
    reportMailDescribeHtml: response.reportMailDescribeHtml,
    diagnosisTemplateId: response.diagnosisTemplateId,
    publishStartAt: response.publishStartAt,
    publishEndAt: response.publishEndAt,
    noIndexFlag: response.noIndexFlag,
    benchmarkContactListId: response.benchmarkContactListId,
    gtmId: response.gtmId,
    respondentFieldGroupId: response.respondentFieldGroupId,
    respondentFields: response.respondentFields.map(field => ({
      ...field,
    })) as unknown as DiagnosisEditFormStateType['respondentFields'],
    lineFlag: response.lineFlag
      ? DivConst.items.lineFlag.enable.value
      : DivConst.items.lineFlag.disable.value,
    lineUrl: response.lineUrl,
    questions: response.questions.map(question => ({
      title: question.title,
      questionImageKey: question.questionImageKey,
      options: question.options.map(option => ({
        title: option.title,
        diagnosisResultList: option.diagnosisResultList,
      })),
    })),
    results: response.results.map(result => ({
      id: result.id,
      title: result.title,
      descriptionHtml: result.descriptionHtml,
      imageKey: result.imageKey,
    })),
  };
}

// ------------------------------------------------------------------ //
// mapAiQuestionsToFormState
// ------------------------------------------------------------------ //

type AiResponse = {
  questions: {
    title: string | null;
    options: {
      title: string | null;
      diagnosisResultList: string | null;
    }[];
  }[];
  results: {
    title: string;
    descriptionHtml: string;
  }[];
};

/**
 * AI 生成のレスポンスをフォーム questions / results 形式に変換する。
 * - results に id: Math.random() / isNew: true / imageKey: null を付与する
 * - questions の options の diagnosisResultList は convResultListTitleToId で title→id に変換する
 * - convResultListTitleToId に渡す results は生成後の results（id 付き）を参照する
 */
export function mapAiQuestionsToFormState(
  aiResponse: AiResponse,
  _currentResults: DiagnosisEditFormStateType['results']
): {
  questions: DiagnosisEditFormStateType['questions'];
  results: DiagnosisEditFormStateType['results'];
} {
  // 先に results を生成して id を確定させる
  const results: DiagnosisEditFormStateType['results'] = aiResponse.results.map(r => ({
    id: Math.random(),
    title: r.title,
    descriptionHtml: r.descriptionHtml,
    imageKey: null,
    isNew: true,
  }));

  const questions: DiagnosisEditFormStateType['questions'] = aiResponse.questions.map(q => ({
    title: q.title ?? undefined,
    questionImageKey: null,
    options: q.options.map(option => ({
      title: option.title ?? undefined,
      diagnosisResultList: convResultListTitleToId(
        option.diagnosisResultList ?? '',
        results as Parameters<typeof convResultListTitleToId>[1]
      ),
    })),
  }));

  return { questions, results };
}

// ------------------------------------------------------------------ //
// buildAiChatCurrentContent
// ------------------------------------------------------------------ //

/**
 * state の判定結果 id CSV を title CSV に変換する（送信側専用ロジック）。
 * 既存の convResultListIdToTitle（utils/diagnosis.ts）は encodeURIComponent を掛けるため使わない
 * （LLM に percent-encoded 文字列が渡ると戻りの convResultListTitleToId の title 一致が外れる）。
 */
function convertResultIdCsvToTitleCsv(
  idCsv: string | null | undefined,
  results: DiagnosisEditFormStateType['results']
): string | null {
  if (!idCsv) return null;
  const ids = idCsv.split(',');
  const titleCsv = results
    .filter(result => ids.includes(String(result.id)))
    .map(result => result.title)
    .join(',');
  return titleCsv || null;
}

/**
 * 診断編集画面のフォーム値を AI チャット送信用の currentContent に変換する。
 * - 出力キーは DiagnosisAiContentInput の全キーのみ（state 余剰キー results.id/isNew/imageKey・
 *   questions.questionImageKey は含めない）
 * - 未設定値は `?? null` で必ずキーを送る
 * - questions に `name: 設問N`（N は 1 始まり）を付与する
 * - options.diagnosisResultList は id CSV → title CSV に変換する
 */
export function buildAiChatCurrentContent(
  values: DiagnosisEditFormStateType
): DiagnosisAiContentInput {
  const results = values.results ?? [];

  return {
    title: values.title ?? null,
    description: values.description ?? null,
    questions: (values.questions ?? []).map((question, index) => ({
      name: `設問${index + 1}`,
      title: question.title ?? null,
      options: (question.options ?? []).map(option => ({
        title: option.title ?? null,
        diagnosisResultList: convertResultIdCsvToTitleCsv(option.diagnosisResultList, results),
      })),
    })),
    results: results.map(result => ({
      title: result.title ?? null,
      descriptionHtml: result.descriptionHtml ?? null,
    })),
  };
}

// ------------------------------------------------------------------ //
// mapAiQuestionToFormQuestion
// ------------------------------------------------------------------ //

/**
 * AI 応答の単一設問（answerContent 経由）をフォーム questions 形式に変換する（パッチ用）。
 * questionImageKey は含まない（呼び出し側が既存値を保持する）。
 * diagnosisResultList は現在の results（id 付き）を参照して title→id に変換する
 * （mapAiQuestionsToFormState は results を id ごと再生成する前提のため単一設問パッチには使えない）。
 */
export function mapAiQuestionToFormQuestion(
  question: AiDiagnosisQuestion,
  currentResults: DiagnosisEditFormStateType['results']
): Pick<DiagnosisEditFormStateType['questions'][number], 'title' | 'options'> {
  return {
    title: question.title ?? undefined,
    options: question.options.map(option => ({
      title: option.title ?? undefined,
      diagnosisResultList: convResultListTitleToId(
        option.diagnosisResultList ?? '',
        currentResults as Parameters<typeof convResultListTitleToId>[1]
      ),
    })),
  };
}
